package com.downdogapp.client.controllers.start

import com.downdogapp.*
import com.downdogapp.client.*
import com.downdogapp.client.api.Pose
import com.downdogapp.client.controllers.*
import com.downdogapp.client.singleton.App
import com.downdogapp.client.singleton.Network
import com.downdogapp.client.views.start.NewPracticePageView
import kotlin.math.pow
import kotlin.math.roundToInt

object NewPracticePage : Page {

  override val view get() = internalView

  val CIRCLE_RADIUS = if (Util.isSmallPhone) 115 else 130
  val CIRCLE_LINE_WIDTH = 5
  val CIRCLE_INSET = 20

  val CIRCLE_OFFSET_ANGLE = Angle.fromDegrees(90.0) // Top of circle is 270 degrees

  val lengthIsChangeable
    get() = SequenceSettings.getOptions(MANIFEST.sequenceLengthTypeId).size >= 2

  private val minAngle get() = Angle.fromMinutes(if (SequenceSettings.lengthOptionIds.isEmpty()) 0 else SequenceSettings.lengthOptionIds.first())

  private val maxAngle get() = Angle.fromMinutes(if (SequenceSettings.lengthOptionIds.isEmpty()) 60 else SequenceSettings.lengthOptionIds.last())

  val displayAngle
    get() =
      if (hasStartedUp)
        currentAngle.coerceIn(minAngle, maxAngle)
      else
        currentAngle.coerceIn(Angle.ZERO, maxOf(maxAngle, Angle.fromDegrees(360)))

  val circleGradientStartColor
    get() =
      Color.circleBlue(0.5 + 0.5 * (displayAngle.degrees - 360.0).coerceAtLeast(0.0) / 270.0)

  val circleGradientEndColor
    get() =
      Color.circleBlue(0.5 + 0.5 * displayAngle.degrees / 270.0)

  val selectedLengthOptionLabel
    get() = if (hasStartedUp)
      SequenceSettings.getSelectedLength()
    else
      (currentAngle.degrees / 6).roundToInt().toString()

  fun animateIn() {
    logEvent("starting_length_circle_animation")
    if (!lengthIsChangeable) {
      hasStartedUp = true
      updateVisibleAngle(Angle.fromMinutes(SequenceSettings.selectedLengthOptionId))
      view.updateCircleLabel(false)
      return
    }
    val targetMinutes = SequenceSettings.selectedLengthOptionId.toDouble()
    val totalAnimationTime = seconds(targetMinutes.pow(1 / 3.0) / 3)
    updateVisibleAngle(Angle.fromDegrees(1))

    App.runAfter(seconds(0.1)) {
      val startClockTime = currentClockTime()

      fun animateStep() {
        val proportion = (currentClockTime() - startClockTime) / totalAnimationTime
        if (proportion >= 1.0) {
          updateVisibleAngle(Angle.fromMinutes(targetMinutes))
          hasStartedUp = true
        } else {
          val updatedAngle = Angle.fromMinutes(targetMinutes * bezierCubic(proportion))
          if (currentAngle <= updatedAngle && !hasStartedUp) {
            updateVisibleAngle(updatedAngle)
          }
          App.runAfter(millis(10)) { animateStep() }
        }
        view.updateCircleLabel(false)
      }

      animateStep()
    }
  }

  fun showDrawerTooltip(onCollapsedDrawer: () -> Unit) {
    App.runAfter(seconds(1.0)) {
      StartViewController.goToPage(this)
      view.expandDrawer(showTooltip = true, onCollapsedDrawer = onCollapsedDrawer)
    }
  }

  // proportion should be in [0, 1]
  // https://easings.net/#easeInOutCubic
  fun bezierCubic(proportion: Double) =
    if (proportion < 0.5)
      4 * proportion.pow(3)
    else
      1.0 - (-2.0 * proportion + 2).pow(3) / 2.0

  // exactly inverses the function bezierCurve
  fun inverseBezierCubic(proportion: Double): Double {
    return if (proportion <= 0.5)
      (proportion / 4.0).pow(1.0 / 3.0)
    else
      1.0 - inverseBezierCubic(1.0 - proportion)
  }

  fun onTouchCircle(angle: Angle) {
    hasStartedUp = true
    val newAngle = (currentAngle + delta(currentAngle, angle)).let {
      if (it < Angle.ZERO) it + Angle.fromDegrees(360) else it
    }.coerceIn(minAngle, maxAngle)
    updateTimeFromAngle(newAngle, false)
  }

  fun onMoveCircle(angle: Angle) {
    updateTimeFromAngle(currentAngle + delta(currentAngle, angle), false)
  }

  fun onReleaseCircle(angle: Angle) {
    updateTimeFromAngle((currentAngle + delta(currentAngle, angle)).coerceIn(minAngle, maxAngle), true)
  }

  override fun refreshPage() {
    if (hasStartedUp) {
      currentAngle = Angle.fromMinutes(SequenceSettings.selectedLengthOptionId)
    }
    view.refreshView()
  }

  override fun onBackClicked() =
    view.collapseDrawer()

  override fun onScrolledFrom() {
    view.collapseDrawer()
  }

  override fun onForegrounded() {
    view.finishDrawerAnimation()
  }

  fun startPractice(sequenceId: String) {
    validateCanStartPractice {
      logEvent("start")
      App.goToExistingSequence(sequenceId)
    }
  }

  fun validateCanStartPractice(checkOffline: Boolean = true, action: () -> Unit) {
    when {
      checkOffline && Network.isOffline() -> {
        App.alertNoInternet(Strings.START_PRACTICE_NO_INTERNET_MESSAGE)
      }

      MANIFEST.displayMembershipOnStartClicked -> {
        App.goToPurchase {
          if (!MANIFEST.displayMembershipOnStartClicked) {
            validateCanStartPractice(checkOffline, action)
          }
        }
      }

      HealthWaiverViewController.acceptedHealthWaiver -> {
        action()
      }

      else -> {
        App.pushViewController(
          HealthWaiverViewController {
            if (HealthWaiverViewController.acceptedHealthWaiver) {
              validateCanStartPractice(checkOffline, action)
            }
          }
        )
      }
    }
  }

  fun startPracticeClicked() {
    validateCanStartPractice {
      logEvent("start")
      App.goToNewSequence()
    }
  }

  fun selectorClicked(typeId: Int) {
    logClick("selector_clicked", mapOf("type_id" to typeId.toString()))
    when (typeId) {
      MANIFEST.mixPresetTypeId -> App.pushViewController(
        MixViewController(
          selectorItem = SequenceSettings.getSelectorItem(typeId)!!,
          onFinishFn = {
            StartViewController.refreshView()
          }
        )
      )

      MANIFEST.viewPosesTypeId -> {
        StartViewController.showSpinner()
        SequenceSettings.useSequenceResponse { response ->
          StartViewController.hideSpinner()
          if (response?.allPoses == null) {
            App.alertNoInternet()
          } else {
            App.pushViewController(
              PoseListViewController(
                title = Strings.ALL_POSES,
                unfilteredItems = response.allPoses
                  .sortedWith(
                    compareByDescending<Pose> { it.isLikeable && SequenceSettings.getPoseLiked(it.poseId) == true }
                      .thenByDescending { it.isLikeable && SequenceSettings.getPoseLiked(it.poseId) == false }
                      .thenBy { it.name }
                  )
              )
            )
          }
        }
      }

      else -> App.pushViewController(
        SelectorViewController(
          onOptionSelected = {
            StartViewController.refreshView()
          },
          settingTypeId = typeId
        )
      )
    }
  }

  private fun updateTimeFromAngle(angle: Angle, snapAngleToTime: Boolean) {
    setLengthOption(SequenceSettings.lengthOptionIds.minBy { (Angle.fromMinutes(it) - angle).abs() })
    updateVisibleAngle(if (snapAngleToTime) Angle.fromMinutes(SequenceSettings.selectedLengthOptionId) else angle)
  }

  private fun updateVisibleAngle(angle: Angle) {
    currentAngle = angle
    view.updateCircleAngle()
  }

  private fun setLengthOption(id: Int) {
    if (id != SequenceSettings.getStoredId(MANIFEST.sequenceLengthTypeId)) {
      SequenceSettings.saveSetting(MANIFEST.sequenceLengthTypeId, id)
      SequenceSettings.saveSelectorToRecents(MANIFEST.sequenceLengthTypeId)
      view.updateCircleLabel(true)
    }
  }

  private fun delta(orig: Angle, new: Angle) =
    (new - orig).clampedDegrees.let { Angle(if (it < 120) it else it - 360) }

  override val title: String
    get() = MANIFEST.newPracticePageTitle

  override val shouldDisplay: Boolean
    get() = MANIFEST.displayNewPracticePage
}

private val internalView by lazy { NewPracticePageView() }
private var hasStartedUp = false
private var currentAngle = Angle.ZERO
